import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Plus } from 'lucide-react';
import { Resource } from '../../lib/resources';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';

interface ManualEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  resources: Resource[];
  onEntryCreated: () => void;
}

export default function ManualEntryModal({ isOpen, onClose, resources, onEntryCreated }: ManualEntryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [selectedResource, setSelectedResource] = useState('');
  
  // Split date and time states
  const [clockInDate, setClockInDate] = useState('');
  const [clockInTime, setClockInTime] = useState('09:00');  // Default to 9:00 AM
  const [clockOutDate, setClockOutDate] = useState('');
  const [clockOutTime, setClockOutTime] = useState('17:00');  // Default to 5:00 PM
  const [breakStartDate, setBreakStartDate] = useState('');
  const [breakStartTime, setBreakStartTime] = useState('');
  const [breakEndDate, setBreakEndDate] = useState('');
  const [breakEndTime, setBreakEndTime] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Handle clock in date change and update clock out date
  const handleClockInDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setClockInDate(newDate);
    // Auto-set clock out date to match clock in date
    setClockOutDate(newDate);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedResource || !clockInDate || !clockInTime || !clockOutDate || !clockOutTime || !selectedBusiness?.id || !supabase) {
      setError('Please fill in all required fields');
      return;
    }

    // Combine date and time strings
    const clockIn = `${clockInDate}T${clockInTime}`;
    const clockOut = `${clockOutDate}T${clockOutTime}`;
    const breakStart = breakStartDate && breakStartTime ? `${breakStartDate}T${breakStartTime}` : '';
    const breakEnd = breakEndDate && breakEndTime ? `${breakEndDate}T${breakEndTime}` : '';

    // Validate break times if either is provided
    if ((breakStart && !breakEnd) || (!breakStart && breakEnd)) {
      setError('Both break start and end times must be provided if using breaks');
      return;
    }

    if (breakStart && breakEnd) {
      const breakStartDate = new Date(breakStart);
      const breakEndDate = new Date(breakEnd);
      const clockInDate = new Date(clockIn);
      const clockOutDate = new Date(clockOut);

      // Validate break times are within clock in/out period
      if (breakStartDate < clockInDate || breakEndDate > clockOutDate) {
        setError('Break times must be within the clock in/out period');
        return;
      }

      // Validate break end is after break start
      if (breakEndDate <= breakStartDate) {
        setError('Break end time must be after break start time');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Get resource's details for rate information
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', selectedResource)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');

      const clockInDateTime = new Date(clockIn);
      const clockOutDateTime = new Date(clockOut);
      
      // Calculate total minutes and earnings
      let totalMs = clockOutDateTime.getTime() - clockInDateTime.getTime();
      
      // Subtract break time if provided
      if (breakStart && breakEnd) {
        const breakStartDateTime = new Date(breakStart);
        const breakEndDateTime = new Date(breakEnd);
        const breakMs = breakEndDateTime.getTime() - breakStartDateTime.getTime();
        totalMs -= breakMs;
      }
      
      const totalMinutes = Math.floor(totalMs / (1000 * 60));
      const hours = totalMinutes / 60;
      const earnings = hours * (resourceData.hourly_rate || 0);

      // Create the manual entry
      const { error: insertError } = await supabase
        .from('payroll_page')
        .insert({
          business_id: selectedBusiness?.id,
          resource_id: selectedResource,
          clock_in: clockInDateTime.toISOString(),
          clock_out: clockOutDateTime.toISOString(),
          break_start: breakStart ? new Date(breakStart).toISOString() : null,
          break_end: breakEnd ? new Date(breakEnd).toISOString() : null,
          entry_type: 'timeclock',
          status: 'completed',
          process_status: 'UNPAID',
          compensation_type: resourceData.compensation_type || 'hourly',
          rate_amount: resourceData.hourly_rate || 0,
          total_minutes: totalMinutes,
          total_hours: hours,
          total_earnings: earnings,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (insertError) throw insertError;

      onEntryCreated();
      onClose();
    } catch (err) {
      console.error('Error creating manual entry:', err);
      setError('Failed to create manual entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-xl">
                <Plus className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Manual Time Entry</h3>
                <p className="text-sm text-gray-600 mt-1">Create a new time clock entry</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Resource Selection */}
          <div className="space-y-2">
            <label htmlFor="resource" className="block text-sm font-semibold text-gray-700">
              Resource *
            </label>
            <select
              id="resource"
              value={selectedResource}
              onChange={(e) => setSelectedResource(e.target.value)}
              className="w-full px-4 py-3 text-sm bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 transition-all duration-200"
              required
            >
              <option value="">Select a resource</option>
              {resources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.first_name} {resource.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Time Entries */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Clock In */}
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-green-500/10 rounded-lg">
                  <Clock className="h-4 w-4 text-green-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Clock In *</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="clockInDate" className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    id="clockInDate"
                    value={clockInDate}
                    onChange={handleClockInDateChange}
                    className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="clockInTime" className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                  <input
                    type="time"
                    id="clockInTime"
                    value={clockInTime}
                    onChange={(e) => setClockInTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Clock Out */}
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-red-500/10 rounded-lg">
                  <Clock className="h-4 w-4 text-red-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Clock Out *</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="clockOutDate" className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    id="clockOutDate"
                    value={clockOutDate}
                    onChange={(e) => setClockOutDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="clockOutTime" className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                  <input
                    type="time"
                    id="clockOutTime"
                    value={clockOutTime}
                    onChange={(e) => setClockOutTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Break Times */}
          <div className="bg-gray-50/60 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">Break Time (Optional)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Break Start */}
              <div>
                <h5 className="text-xs font-medium text-gray-600 mb-3">Break Start</h5>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="breakStartDate" className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input
                      type="date"
                      id="breakStartDate"
                      value={breakStartDate}
                      onChange={(e) => setBreakStartDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="breakStartTime" className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                    <input
                      type="time"
                      id="breakStartTime"
                      value={breakStartTime}
                      onChange={(e) => setBreakStartTime(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    />
                  </div>
                </div>
              </div>

              {/* Break End */}
              <div>
                <h5 className="text-xs font-medium text-gray-600 mb-3">Break End</h5>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="breakEndDate" className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input
                      type="date"
                      id="breakEndDate"
                      value={breakEndDate}
                      onChange={(e) => setBreakEndDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="breakEndTime" className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                    <input
                      type="time"
                      id="breakEndTime"
                      value={breakEndTime}
                      onChange={(e) => setBreakEndTime(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="manual-entry-form"
              disabled={isSubmitting}
              onClick={handleSubmit}
              className={`px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Creating...' : 'Create Entry'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 